import {
	DateObject,
	type FanShopProductResponseLite,
	type FanShopProductVariant,
	isWeb,
	useIntl,
	useSettings
} from '@capsicum/common';
import { hasEnoughTokenCombinedWallet, useWalletFanToken } from '@capsicum/features/wallet/shared';
import { type NCWBalanceType, usePartners, useWalletsBalanceWrapper } from '@capsicum/socios-core';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ProductTypes } from '../../models';
import { getPriceForVariant } from '../../util';
import { useCategories } from '../useCategories';

type UseProductCardProps = {
	getTokenBalanceBy?: NCWBalanceType['getTokenBalanceBy'];
	variant: FanShopProductVariant;
};

export const useProductCard = ({ getTokenBalanceBy = () => undefined, variant }: UseProductCardProps) => {
	const { $t, getLocalizedField } = useIntl();
	const {
		settings: { isPoblanoEnabled, preventBuyFanTokensModal }
	} = useSettings();
	const { getPartnerBy } = usePartners();
	const [isComingSoon, setIsComingSoon] = useState<boolean>();
	const { getSubcategoryBy } = useCategories();

	const getMainImage = useCallback(
		(v: FanShopProductVariant = variant || {}) => {
			const checkImages = (objWithImages?: FanShopProductResponseLite | FanShopProductVariant) => {
				return objWithImages?.images
					?.filter(image =>
						isWeb()
							? image?.imageType?.type?.includes('desktop')
							: !image?.imageType?.type?.includes('desktop')
					)
					?.find(image => image?.imageType?.type?.includes('_image_'));
			};

			return checkImages(v) || checkImages(v?.product);
		},
		[variant]
	);

	const { getCurrencyBy } = useWalletsBalanceWrapper();

	const tokensRequired = useMemo(() => variant?.product?.productTierLevel?.minTokenRequired || 0, [variant]);
	const variantImage = useMemo(() => getMainImage(variant)?.url || '', [variant, getMainImage]);
	const variantBlurHash = useMemo(() => getMainImage(variant)?.blurHash || '', [variant, getMainImage]);
	const variantPrice = useMemo(
		() => getPriceForVariant(variant?.sellingPrice, variant?.product?.sellingPrice),
		[variant]
	);
	const packageProductPrice = useMemo(
		() => Math.trunc(Number(variant?.product?.sellingPrice ?? 0) * (variant?.product?.quantityStep ?? 0)),
		[variant]
	);
	const variantTitle = useMemo(() => variant?.label || '', [variant]);
	const partnerId = useMemo(() => variant?.product?.partnerId, [variant]);
	const currentPartner = useMemo(
		() => getPartnerBy(p => p.id === variant?.product?.partnerId),
		[getPartnerBy, variant?.product?.partnerId]
	);
	const partnerCurrencyId = currentPartner?.currencyId;
	const { data: partnerWallet, isPending: isPendingCWallet } = useWalletFanToken(partnerCurrencyId);
	const { hasEnoughToken } = partnerWallet ?? {};
	const subcategory = useMemo(
		() => getSubcategoryBy(s => s?.id === variant?.product?.categories?.[0]?.id),
		[getSubcategoryBy, variant]
	);
	const partnerCurrency = useMemo(
		() => getCurrencyBy(c => c.currency.id === partnerCurrencyId),
		[getCurrencyBy, partnerCurrencyId]
	);

	const NCWPartnerTokens = useMemo(
		() => getTokenBalanceBy(p => p.shortCode === partnerCurrency?.currency.shortCode),
		[getTokenBalanceBy, partnerCurrency?.currency.shortCode]
	);

	const balance = useMemo(
		() => Number(partnerWallet?.totalBalance || 0) + Number(NCWPartnerTokens?.amount || 0),
		[partnerWallet?.totalBalance, NCWPartnerTokens?.amount]
	);

	const hasEnoughFanTokens = useCallback(() => {
		if (isPoblanoEnabled) {
			return hasEnoughTokenCombinedWallet(tokensRequired, balance);
		} else {
			return hasEnoughToken?.(tokensRequired);
		}
	}, [tokensRequired, balance, hasEnoughTokenCombinedWallet, hasEnoughToken]);

	const ribbonColor = subcategory?.color;
	const ribbonText = getLocalizedField('name', subcategory || {}) || subcategory?.name;

	const redeemableAt = variant?.redeemableAt;

	const isTicketType = variant?.product?.productType?.name === ProductTypes.Ticket;
	const isReverseAuction = variant?.product?.redeemType === 'reverseAuction';
	const isPackageProduct = variant?.product?.isPackage;

	const tokenRequiredAmount = variant?.product?.productTierLevel?.minTokenRequired || 0;

	const hasEnoughStock = useMemo(
		() => (variant?.availableStock ? variant?.availableStock > 0 : null),
		[variant?.availableStock]
	);

	useEffect(() => {
		if (redeemableAt && new DateObject(redeemableAt).getTime() > DateObject.now()) {
			setIsComingSoon(true);
		}
	}, [redeemableAt]);

	const amountNeeded = useMemo(
		() => (tokensRequired - balance > 0 ? tokensRequired - balance : 0),
		[tokensRequired, balance]
	);

	const texts = useMemo(
		() => ({
			availableIn: $t('rewards.fanshop.availableIn'),
			comingSoon: $t('rewards.fanshop.comingSoon'),
			fanTokensNeededText: $t('marketplace.tokenBalance.fanTokensNeeded'),
			getFanTokensText: $t('marketplace.tokenBalance.getFanTokens', {
				shortName: currentPartner?.shortName
			}),
			minimumAmount: `${amountNeeded} ${currentPartner?.shortName}`,
			outOfStock: $t('rewards.fanshop.outOfStock')
		}),
		[$t, amountNeeded, variant, currentPartner]
	);

	return {
		balance,
		currentPartner,
		hasEnoughFanTokens,
		hasEnoughStock,
		isComingSoon,
		isPackageProduct,
		isPendingCWallet,
		isReverseAuction,
		isTicketType,
		packageProductPrice,
		partnerCurrencyId,
		partnerId,
		partnerWallet,
		preventBuyFanTokensModal,
		redeemableAt,
		ribbonColor,
		ribbonText,
		setIsComingSoon,
		texts,
		tokenRequiredAmount,
		variantBlurHash,
		variantImage,
		variantPrice,
		variantTitle
	};
};
